import { useEffect, useRef, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { INDIVIDUAL_USER_TYPE, COUNTRY_LIST, PROVINCES_LIST } from 'constants'
import HCaptcha from '@hcaptcha/react-hcaptcha'

import { parseAndSetFormErrors, captureTrackingData } from '../../utils'
import {
  Button,
  Container,
  ContentBox,
  Fields,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
  Divider,
} from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'

export function AdvisorSignUp() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const captchaRef = useRef()
  const [captchaToken, setCaptchaToken] = useState()
  const trackingData = captureTrackingData()

  const { data: { captcha: captchaSiteKey } = {}, refetch: refetchCaptcha } = useQuery(gql`
    query Captcha {
      captcha
    }
  `)

  const {
    data: inviteData,
    loading: inviteLoading,
    error: inviteError,
  } = useQuery(queries.onboarding.advisorInviteContext, {
    variables: { token },
    fetchPolicy: 'network-only',
  })

  const inviteContext = inviteData?.advisorInviteContext

  const { register, handleSubmit, formState, setError, getValues, setValue, watch, control, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      address: { country: 'Canada' },
    },
  })

  // Pre-fill form once invite context loads
  useEffect(() => {
    if (!inviteContext) return

    reset({
      custodianName: inviteContext.custodianName || '',
      firstName: '',
      lastName: '',
      email: inviteContext.targetEmail || '',
      advisorDealerRepCode: inviteContext.advisorDealerRepCode || '',
      phone: inviteContext.advisorPhone || '',
      address: {
        lineOne: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
      },
      password: '',
      passwordConfirmation: '',
    })
  }, [inviteContext, reset])

  const { errors = {} } = formState

  const [signUp, { loading }] = useMutation(mutations.user.signUp, {
    onCompleted: () => {
      navigate('/sign-up/success')
    },
    onError: async (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)
      captchaRef?.current?.resetCaptcha?.()

      if (newErrors?.message.includes('already has an account')) {
        setAlert({
          message: 'An account with this email address already exists. Please log in to accept this invitation.',
          severity: 'error',
          action: {
            label: 'Sign In',
            onClick: () => navigate(`/advisor/accept/${token}`),
          },
        })
      } else if (newErrors?.message.includes('Email taken')) {
        setAlert({ message: 'An account with this email address already exists.', severity: 'error' })
      } else if (newErrors?.message === 'captcha required') {
        setAlert({ message: 'Please fill out the captcha on this form.', severity: 'error' })
        if (!captchaSiteKey) await refetchCaptcha()
      } else {
        setAlert({ message: newErrors?.message || 'Signup Failed', severity: 'error' })
      }
    },
  })

  const onSubmit = ({
    custodianName,
    firstName,
    lastName,
    email,
    advisorDealerRepCode,
    phone,
    password,
    passwordConfirmation,
    address,
  }) => {
    signUp({
      variables: {
        data: {
          firstName,
          lastName,
          email,
          phone,
          password,
          passwordConfirmation,
          address,
          userType: INDIVIDUAL_USER_TYPE,
          captcha: captchaToken,
          inviteToken: token,
          advisorCustodianSettings: {
            custodianName,
            advisorName: `${firstName} ${lastName}`.trim(),
            advisorEmail: email,
            advisorPhone: phone,
            advisorDealerRepCode: advisorDealerRepCode || undefined,
            address: {
              lineOne: address.lineOne,
              city: address.city,
              province: address.province,
              postalCode: address.postalCode,
            },
          },
          ...trackingData,
        },
      },
    })
  }

  // Loading state
  if (inviteLoading) {
    return (
      <Container maxWidth="sm">
        <Stack spacing={2} style={{ padding: 16 }}>
          <Text.H5>Loading invitation...</Text.H5>
        </Stack>
      </Container>
    )
  }

  // Error states
  if (inviteError) {
    const message = inviteError.message || ''
    const isExpired = message.includes('expired')
    const isUsed = message.includes('already been used')
    const isCancelled = message.includes('cancelled')

    if (isCancelled) {
      return (
        <Container maxWidth="sm">
          <Stack spacing={2} style={{ padding: 16 }}>
            <Text.H5>Invitation Cancelled</Text.H5>
            <Text.Body>
              This invitation has been cancelled by the donor. If you believe this is a mistake, please contact them
              directly or reach out to <Link href="mailto:support@givewise.ca">support@givewise.ca</Link>.
            </Text.Body>
          </Stack>
        </Container>
      )
    }

    if (isExpired) {
      return (
        <Container maxWidth="sm">
          <Stack spacing={2} style={{ padding: 16 }}>
            <Text.H5>Invitation Expired</Text.H5>
            <Text.Body>
              This invitation link has expired. Please contact{' '}
              <Link href="mailto:support@givewise.ca">support@givewise.ca</Link> for assistance.
            </Text.Body>
          </Stack>
        </Container>
      )
    }

    if (isUsed) {
      return (
        <Container maxWidth="sm">
          <Stack spacing={2} style={{ padding: 16 }}>
            <Text.H5>Invitation Already Used</Text.H5>
            <Text.Body>
              This invitation has already been used. If you already have an account, please{' '}
              <Link href="/login">sign in</Link>.
            </Text.Body>
          </Stack>
        </Container>
      )
    }

    return (
      <Container maxWidth="sm">
        <Stack spacing={2} style={{ padding: 16 }}>
          <Text.H5>Invalid Invitation</Text.H5>
          <Text.Body>
            This invitation link is invalid. You can still <Link href="/sign-up">create an account</Link> using the
            standard sign-up page.
          </Text.Body>
        </Stack>
      </Container>
    )
  }

  if (inviteContext?.targetAlreadyClaimed) {
    return (
      <Container maxWidth="sm">
        <Stack spacing={2} style={{ padding: 16 }}>
          <Text.H5>You Already Have an Account</Text.H5>
          <Text.Body>
            An account already exists for {inviteContext.targetEmail}. Please log in to accept this advisor connection.
          </Text.Body>
          <Button color="info" to={`/advisor/accept/${token}`}>
            Sign In
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} style={{ padding: 16 }}>
        <Text.H5 component="h1">Create Your Advisor Account</Text.H5>
        {inviteContext?.donorName && (
          <Text.Body>{inviteContext.donorName} has identified you as their financial advisor.</Text.Body>
        )}

        <ContentBox border>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack alignItems="flex-start">
              <Text.Bold>Name of Securities Firm</Text.Bold>

              <Fields.Text
                label="Name of Securities Firm *"
                InputLabelProps={{ shrink: true }}
                error={!!errors.custodianName}
                helperText={errors?.custodianName?.message}
                {...register('custodianName', {
                  required: 'This field is required',
                  validate: {
                    noSpecialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
                  },
                })}
              />

              <Divider variant="middle" flexItem sx={{ my: 2 }} />

              <Text.Bold>Advisor Information</Text.Bold>

              <Fields.Text
                label="First Name *"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                {...register('firstName', { ...validations.required })}
              />
              <Fields.Text
                label="Last Name *"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                {...register('lastName', { ...validations.required })}
              />
              <Fields.Text
                label="Email *"
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register('email', { ...validations.required, ...validations.email })}
              />
              <Fields.Text
                label="Dealer Rep Code"
                error={!!errors.advisorDealerRepCode}
                helperText={errors?.advisorDealerRepCode?.message}
                {...register('advisorDealerRepCode')}
              />
              <Fields.Text
                type="text"
                label="Phone Number *"
                error={!!errors.phone}
                helperText={errors?.phone?.message}
                {...register('phone', { ...validations.required, ...validations.phoneNumber })}
              />

              <Divider variant="middle" flexItem sx={{ my: 2 }} />

              <Text.Bold>Brokerage/Custodian Address</Text.Bold>

              <Fields.Text
                label="Address Line One *"
                error={!!errors.address?.lineOne}
                helperText={errors?.address?.lineOne?.message}
                {...register('address.lineOne', { ...validations.required })}
              />
              <Fields.Text
                label="Address Line Two"
                error={!!errors.address?.lineTwo}
                helperText={errors?.address?.lineTwo?.message}
                {...register('address.lineTwo')}
              />
              <Fields.Text
                label="City *"
                error={!!errors.address?.city}
                helperText={errors?.address?.city?.message}
                {...register('address.city', { ...validations.required })}
              />

              {getValues('address.country') === 'Canada' ? (
                <FormControl fullWidth>
                  <InputLabel id="provinceLabel">Province *</InputLabel>
                  <Controller
                    control={control}
                    name="province"
                    render={() => (
                      <Select
                        value={watch('address.province') || ''}
                        onChange={(e) => setValue('address.province', e.target.value, { shouldValidate: true })}
                        label="Province *"
                        labelId="provinceLabel"
                        error={!!errors.address?.province}
                        defaultValue=""
                        required
                      >
                        {PROVINCES_LIST.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              ) : (
                <Fields.Text
                  label="Province/State *"
                  value={watch('address.province') || ''}
                  onChange={(e) => setValue('address.province', e.target.value, { shouldValidate: true })}
                  error={!!errors.address?.province}
                  helperText={errors?.address?.province?.message}
                />
              )}

              <Fields.Text
                label="Postal Code *"
                error={!!errors.address?.postalCode}
                helperText={errors?.address?.postalCode?.message}
                {...register('address.postalCode', { ...validations.required })}
              />
              <FormControl fullWidth>
                <InputLabel id="countryLabel">Country *</InputLabel>
                <Controller
                  name="country"
                  control={control}
                  render={() => (
                    <Select
                      defaultValue="Canada"
                      value={watch('address.country') || 'Canada'}
                      onChange={(e) => {
                        setValue('address.country', e.target.value, { shouldValidate: true })
                        setValue('address.province', '', { shouldValidate: true })
                      }}
                      label="Country *"
                      labelId="countryLabel"
                    >
                      {COUNTRY_LIST.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <Divider variant="middle" flexItem sx={{ my: 2 }} />

              <Text.Bold>Password</Text.Bold>

              <Fields.NewPassword register={register} errors={errors} getValues={getValues} />

              {captchaSiteKey && <HCaptcha sitekey={captchaSiteKey} onVerify={setCaptchaToken} ref={captchaRef} />}

              <Row justifyContent="flex-end" spacing={4}>
                <Text.Body>
                  By clicking &ldquo;Sign Up&rdquo;, you agree to our{' '}
                  <a
                    href="https://guide.givewise.ca/policy-and-user-documents/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </Text.Body>
                <Button type="submit" disabled={loading} sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                  Sign Up
                </Button>
                {errors.form && <FormHelperText error={!!errors.form}>{errors.form.message}</FormHelperText>}
              </Row>
              <Alert {...alertProps} />
            </Stack>
          </form>
          <Divider variant="middle" flexItem sx={{ mb: 4, mt: 4 }} />
          <Row
            justifyContent="flex-end"
            spacing={4}
            sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
          >
            <Text.Body>Already have an account with us?</Text.Body>
            <Button color="info" to={`/advisor/accept/${token}`}>
              Sign In
            </Button>
          </Row>
        </ContentBox>
      </Stack>
    </Container>
  )
}
